import React, { useState, useEffect } from "react";
import { User, Activity, Clock, Calendar, Filter, TrendingUp } from "lucide-react";
import { getAsperoLogsByUser } from "@/services/api/aspero";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

/**
 * User Activity Timeline - Shows webhook activity for a specific user
 * This component ONLY calls user-specific APIs, not system-wide stats
 */
const UserActivityTimeline = ({ clientUuid, userName = "User" }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [eventFilter, setEventFilter] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  const fetchUserLogs = async () => {
    if (!clientUuid) {
      setError("Client UUID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        sort: "createdAt",
        order: "asc", // Chronological order for timeline
      };

      if (eventFilter) params.eventName = eventFilter;
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await getAsperoLogsByUser(clientUuid, params);

      if (response?.data?.success) {
        setLogs(response.data.data || []);
        setTotal(response.data.meta?.total || 0);
        setTotalPages(response.data.meta?.pages || 1);
        setError(null);
      } else {
        setError("Failed to fetch user activity");
      }
    } catch (err) {
      console.error("User activity fetch failed:", err);
      setError(err?.response?.data?.message || "Failed to fetch user activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserLogs();
  }, [clientUuid, page, eventFilter, dateRange]);

  // Calculate user-specific stats from logs
  const userStats = React.useMemo(() => {
    if (!logs.length) return null;

    const eventCounts = {};
    logs.forEach((log) => {
      const eventName = log.webhookData?.eventName;
      if (eventName) {
        eventCounts[eventName] = (eventCounts[eventName] || 0) + 1;
      }
    });

    const uniqueEvents = Object.keys(eventCounts).length;
    const mostFrequentEvent = Object.entries(eventCounts).sort(
      (a, b) => b[1] - a[1]
    )[0];

    return {
      totalEvents: logs.length,
      uniqueEvents,
      mostFrequentEvent: mostFrequentEvent
        ? { name: mostFrequentEvent[0], count: mostFrequentEvent[1] }
        : null,
    };
  }, [logs]);

  const getEventBadgeColor = (eventName) => {
    if (!eventName) return "bg-gray-500";
    if (
      eventName.includes("SUCCESS") ||
      eventName.includes("VERIFIED") ||
      eventName.includes("COMPLETED")
    ) {
      return "bg-green-500";
    }
    if (eventName.includes("FAILED") || eventName.includes("REJECTED")) {
      return "bg-red-500";
    }
    if (
      eventName.includes("INITIATED") ||
      eventName.includes("STARTED") ||
      eventName.includes("SUBMITTED")
    ) {
      return "bg-blue-500";
    }
    return "bg-gray-500";
  };

  if (loading && !logs.length) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error && !logs.length) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-red-200">
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* User Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 rounded-full p-3 mr-4">
              <User className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{userName}</h2>
              <p className="text-blue-100 text-sm font-mono">{clientUuid}</p>
            </div>
          </div>
          <button
            onClick={fetchUserLogs}
            disabled={loading}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* User-Specific Stats */}
      {userStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {total.toLocaleString()}
            </p>
            <p className="text-xs text-gray-600">Total Events</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {userStats.uniqueEvents}
            </p>
            <p className="text-xs text-gray-600">Unique Event Types</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-lg font-bold text-gray-900 truncate">
              {userStats.mostFrequentEvent?.name || "N/A"}
            </p>
            <p className="text-xs text-gray-600">Most Frequent Event</p>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Activity Timeline
        </h3>

        {logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No activity found for this user</p>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log, index) => (
              <div
                key={log._id}
                className="flex items-start border-l-4 border-blue-500 pl-4 py-2 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0 mt-1">
                  <div
                    className={`w-3 h-3 rounded-full ${getEventBadgeColor(
                      log.webhookData?.eventName
                    )}`}
                  />
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getEventBadgeColor(
                        log.webhookData?.eventName
                      )}`}
                    >
                      {log.webhookData?.eventName?.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(log.createdAt).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  {log.webhookData?.eventData &&
                    Object.keys(log.webhookData.eventData).length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        {Object.entries(log.webhookData.eventData).map(
                          ([key, value]) => (
                            <span key={key} className="mr-3">
                              <span className="font-medium">{key}:</span>{" "}
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : value}
                            </span>
                          )
                        )}
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t pt-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActivityTimeline;
